// Custom Select [v0.0.0]

import * as SelectPrimitives from '@radix-ui/react-select'
import { RiArrowDownSLine, RiArrowUpSLine, RiCheckLine } from '@remixicon/react'
import { format } from 'date-fns'
import {
	forwardRef,
	type ElementRef,
	type ComponentPropsWithoutRef,
} from 'react'

import { type DateRange } from 'react-day-picker'
import { cn, focusInput, hasErrorInput } from '#app/utils/misc.tsx'

const Select = SelectPrimitives.Root
Select.displayName = 'Select'

const SelectGroup = SelectPrimitives.Group
SelectGroup.displayName = 'SelectGroup'

const SelectValue = SelectPrimitives.Value
SelectValue.displayName = 'SelectValue'

const selectTriggerStyles = [
	cn(
		// base
		'group/trigger flex w-full items-center justify-between gap-2 truncate rounded-md border px-3 py-1 text-sm transition outline-none select-none',
		// border color
		'border-gray-300 dark:border-gray-800',
		// text color
		'text-gray-900 dark:text-gray-50',
		// placeholder
		'data-[placeholder]:text-gray-500 data-[placeholder]:dark:text-gray-500',
		// background color
		'bg-white dark:bg-gray-950',
		// hover
		'hover:bg-gray-50 hover:dark:bg-gray-950/50',
		// disabled
		'data-[disabled]:bg-gray-100 data-[disabled]:text-gray-400',
		'data-[disabled]:dark:border-gray-700 data-[disabled]:dark:bg-gray-800 data-[disabled]:dark:text-gray-500',
		focusInput,
		// invalid (optional)
		// "aria-[invalid=true]:dark:ring-red-400/20 aria-[invalid=true]:ring-2 aria-[invalid=true]:ring-red-200 aria-[invalid=true]:border-red-500 invalid:ring-2 invalid:ring-red-200 invalid:border-red-500"
	),
]

const SelectTrigger = forwardRef<
	ElementRef<typeof SelectPrimitives.Trigger>,
	ComponentPropsWithoutRef<typeof SelectPrimitives.Trigger> & {
		hasError?: boolean
	}
>(({ className, hasError, children, ...props }, forwardedRef) => {
	return (
		<SelectPrimitives.Trigger
			ref={forwardedRef}
			className={cn(
				selectTriggerStyles,
				hasError ? hasErrorInput : '',
				className,
			)}
			{...props}
		>
			<span className="truncate">{children}</span>
			<SelectPrimitives.Icon asChild>
				<RiArrowDownSLine
					className={cn(
						// base
						'-mr-1 size-5 shrink-0',
						// text color
						'text-gray-400 dark:text-gray-600',
						// disabled
						'group-data-[disabled]/trigger:text-gray-300 group-data-[disabled]/trigger:dark:text-gray-600',
					)}
				/>
			</SelectPrimitives.Icon>
		</SelectPrimitives.Trigger>
	)
})

SelectTrigger.displayName = 'SelectTrigger'

const SelectScrollUpButton = forwardRef<
	ElementRef<typeof SelectPrimitives.ScrollUpButton>,
	ComponentPropsWithoutRef<typeof SelectPrimitives.ScrollUpButton>
>(({ className, ...props }, forwardedRef) => (
	<SelectPrimitives.ScrollUpButton
		ref={forwardedRef}
		className={cn(
			'flex cursor-default items-center justify-center py-1',
			className,
		)}
		{...props}
	>
		<RiArrowUpSLine className="size-3 shrink-0" aria-hidden="true" />
	</SelectPrimitives.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitives.ScrollUpButton.displayName

const SelectScrollDownButton = forwardRef<
	ElementRef<typeof SelectPrimitives.ScrollDownButton>,
	ComponentPropsWithoutRef<typeof SelectPrimitives.ScrollDownButton>
>(({ className, ...props }, forwardedRef) => (
	<SelectPrimitives.ScrollDownButton
		ref={forwardedRef}
		className={cn(
			'flex cursor-default items-center justify-center py-1',
			className,
		)}
		{...props}
	>
		<RiArrowDownSLine className="size-3 shrink-0" aria-hidden="true" />
	</SelectPrimitives.ScrollDownButton>
))
SelectScrollDownButton.displayName =
	SelectPrimitives.ScrollDownButton.displayName

const SelectContent = forwardRef<
	ElementRef<typeof SelectPrimitives.Content>,
	ComponentPropsWithoutRef<typeof SelectPrimitives.Content>
>(
	(
		{
			className,
			position = 'popper',
			children,
			sideOffset = 8,
			collisionPadding = 10,
			...props
		},
		forwardedRef,
	) => (
		<SelectPrimitives.Portal>
			<SelectPrimitives.Content
				ref={forwardedRef}
				data-slot="popover-content"
				className={cn(
					// base
					'relative z-[60] overflow-hidden rounded-md border',
					// widths
					'max-w-[95vw] min-w-[calc(var(--radix-select-trigger-width)-2px)]',
					// heights
					'max-h-80 max-h-[--radix-select-content-available-height]',
					// background color
					'bg-white dark:bg-gray-950',
					// text color
					'text-gray-900 dark:text-gray-50',
					// border color
					'border-gray-200 dark:border-gray-800',
					// transition
					'will-change-[transform,opacity]',
					// "data-[state=open]:animate-slideDownAndFade",
					'data-[state=closed]:animate-hide',
					'data-[side=bottom]:animate-slideDownAndFade data-[side=left]:animate-slideLeftAndFade data-[side=right]:animate-slideRightAndFade data-[side=top]:animate-slideUpAndFade',
					className,
				)}
				sideOffset={sideOffset}
				position={position}
				collisionPadding={collisionPadding}
				{...props}
			>
				<SelectScrollUpButton />
				<SelectPrimitives.Viewport
					className={cn(
						// padding
						'p-1',
						// make the viewport the scroll container
						'overflow-auto overscroll-contain',
						// ensure we respect available height and width
						position === 'popper' &&
							'max-h-80 max-h-[--radix-select-content-available-height] w-full min-w-[calc(var(--radix-select-trigger-width))]',
					)}
				>
					{children}
				</SelectPrimitives.Viewport>
				<SelectScrollDownButton />
			</SelectPrimitives.Content>
		</SelectPrimitives.Portal>
	),
)

SelectContent.displayName = 'SelectContent'

const SelectGroupLabel = forwardRef<
	ElementRef<typeof SelectPrimitives.Label>,
	ComponentPropsWithoutRef<typeof SelectPrimitives.Label>
>(({ className, ...props }, forwardedRef) => (
	<SelectPrimitives.Label
		ref={forwardedRef}
		className={cn(
			// base
			'px-3 py-2 text-xs font-medium tracking-wide',
			// text color
			'text-gray-500 dark:text-gray-500',
			className,
		)}
		{...props}
	/>
))

SelectGroupLabel.displayName = 'SelectGroupLabel'

const SelectItem = forwardRef<
	ElementRef<typeof SelectPrimitives.Item>,
	ComponentPropsWithoutRef<typeof SelectPrimitives.Item>
>(({ className, children, ...props }, forwardedRef) => {
	return (
		<SelectPrimitives.Item
			ref={forwardedRef}
			className={cn(
				// base
				'grid cursor-pointer grid-cols-[1fr_20px] gap-x-2 rounded px-3 py-1.5 transition-colors outline-none data-[state=checked]:font-semibold sm:text-sm',
				// text color
				'text-gray-900 dark:text-gray-50',
				// disabled
				'data-[disabled]:pointer-events-none data-[disabled]:text-gray-400 data-[disabled]:hover:bg-none dark:data-[disabled]:text-gray-600',
				// focus
				'focus-visible:bg-gray-100 focus-visible:dark:bg-gray-900',
				// hover
				'hover:bg-gray-100 hover:dark:bg-gray-900',
				className,
			)}
			{...props}
		>
			<SelectPrimitives.ItemText className="flex-1 truncate">
				{children}
			</SelectPrimitives.ItemText>
			<SelectPrimitives.ItemIndicator>
				<RiCheckLine
					className="size-4 shrink-0 text-gray-800 dark:text-gray-200"
					aria-hidden="true"
				/>
			</SelectPrimitives.ItemIndicator>
		</SelectPrimitives.Item>
	)
})

SelectItem.displayName = 'SelectItem'

const SelectItemPeriod = forwardRef<
	ElementRef<typeof SelectPrimitives.Item>,
	ComponentPropsWithoutRef<typeof SelectPrimitives.Item> & {
		period?: DateRange | undefined
	}
>(({ className, children, period, ...props }, forwardedRef) => {
	return (
		<SelectPrimitives.Item
			ref={forwardedRef}
			className={cn(
				// base
				'relative flex cursor-pointer items-center rounded py-2 pr-3 pl-8 transition-colors outline-none data-[state=checked]:font-semibold sm:text-sm',
				// text color
				'text-gray-900 dark:text-gray-50',
				// disabled
				'data-[disabled]:pointer-events-none data-[disabled]:text-gray-400 data-[disabled]:hover:bg-none dark:data-[disabled]:text-gray-600',
				// focus
				'focus-visible:bg-gray-100 focus-visible:dark:bg-gray-900',
				// hover
				'hover:bg-gray-100 hover:dark:bg-gray-900',
				className,
			)}
			{...props}
		>
			<span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
				<SelectPrimitives.ItemIndicator>
					<RiCheckLine
						className="size-5 shrink-0 text-gray-800 dark:text-gray-200"
						aria-hidden="true"
					/>
				</SelectPrimitives.ItemIndicator>
			</span>
			<div className="flex w-full items-center">
				{/* adapt width accordingly if you use longer names for periods */}
				<span className="w-40 sm:w-32">
					<SelectPrimitives.ItemText>{children}</SelectPrimitives.ItemText>
				</span>
				<span>
					{period?.from && period?.to && (
						<span className="font-normal whitespace-nowrap text-gray-400">
							{format(period.from, 'MMM d, yyyy')} –{' '}
							{format(period.to, 'MMM d, yyyy')}
						</span>
					)}
				</span>
			</div>
		</SelectPrimitives.Item>
	)
})

SelectItemPeriod.displayName = 'SelectItemPeriod'

const SelectSeparator = forwardRef<
	ElementRef<typeof SelectPrimitives.Separator>,
	ComponentPropsWithoutRef<typeof SelectPrimitives.Separator>
>(({ className, ...props }, forwardedRef) => (
	<SelectPrimitives.Separator
		ref={forwardedRef}
		className={cn(
			// base
			'-mx-1 my-1 h-px',
			// background color
			'bg-gray-300 dark:bg-gray-700',
			className,
		)}
		{...props}
	/>
))

SelectSeparator.displayName = 'SelectSeparator'

export {
	Select,
	SelectContent,
	SelectGroup,
	SelectGroupLabel,
	SelectItem,
	SelectItemPeriod,
	SelectSeparator,
	SelectTrigger,
	SelectValue,
}
